import { Component, computed, input, signal, ViewChild } from '@angular/core';
import { CdkMenuModule, CdkMenuTrigger } from '@angular/cdk/menu';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { GetCompanyListQuery } from 'gql/graphql';
import { Apollo } from 'apollo-angular';
import { getCompanyList } from '@graphql/company';
import { AbbCommonModule } from '../../abb-common.module';
import { CognitoService } from '@services/cognito/cognito.service';
import { OverflowTooltipDirective } from '@directives/overflow-tooltip.directive';
import { getErrorMessage } from '@utils/errors';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@services/ui/toast.service';
import { CompanyService } from '@services/company/company-service.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UserSettingsService } from '@services/user/user-settings.service';
import { Router } from '@angular/router';
import { URLS } from '@constants/urls';
import { PermissionsService } from '@services/user/permissions.service';

@Component({
  selector: 'app-company-select',
  standalone: true,
  host: {
    class: 'w-full flex flex-col overflow-hidden',
  },
  template: `
    <div class="w-full flex flex-col items-center relative">
      <!-- Bottone per aprire il menu -->
      <!-- it would be better to replace this with abb-tooltip -->
      <div class="w-full flex flex-col items-center justify-content-center">
        <span class="w-full flex flex-row items-center">
          <abb-icon
            [cdkMenuTriggerFor]="companySelectMenu"
            name="mat/arrow_left"
            size="small"
            class="mr-2 text-abb-font cursor-pointer"
          ></abb-icon>
          <span
            class="content-center cursor-default text-base font-medium truncate max-w-full"
            overflowTooltip
          >
            @if(!currentCompany()){
            <mat-progress-spinner
              class="mr-2"
              [ngStyle]="{
                '--mdc-circular-progress-active-indicator-color':
                  'rgb(var(--abb-color-black)/0.2)'
              }"
              [diameter]="16"
              [mode]="'indeterminate'"
            ></mat-progress-spinner>
            } @else {
            {{ currentCompany() }}
            }
          </span>
        </span>
      </div>

      <!-- Menu -->
      <ng-template #companySelectMenu>
        <div
          cdkMenu
          class="bg-abb-background mat-elevation-z8 w-full rounded-md absolute max-w-44 left-[0px] transform -translate-x-full top-6"
          (mouseleave)="closeMenuDelayed()"
          (mouseenter)="cancelCloseMenu()"
        >
          <!-- Lista delle aziende -->
          @for (company of companies(); track company.id) {
          <div
            cdkMenuItem
            class="hover:bg-abb-black/[0.04] rounded-md cursor-pointer"
          >
            <abb-button
              variant="clean"
              cdkMenuItem
              class="w-full px-4 py-2"
              (click)="selectCompany(company, $event)"
              [disabled]="company.licenseId === licenseId()"
            >
              {{ company.name }}
            </abb-button>
          </div>
          }
        </div>
      </ng-template>
    </div>
  `,
  imports: [
    CdkMenuModule,
    MatIconModule,
    CommonModule,
    AbbCommonModule,
    OverflowTooltipDirective,
    MatProgressSpinnerModule,
  ],
})
export class CompanySelectComponent {
  @ViewChild(CdkMenuTrigger) menuTrigger!: CdkMenuTrigger;
  licenseId = input.required<string | undefined>();
  currentCompany = computed<string>(() => this.companyService.currentCompany());
  companies = computed<GetCompanyListQuery['getCompanyList']>(() =>
    this.companyService.companies()
  );

  constructor(
    private readonly apollo: Apollo,
    private readonly cognitoService: CognitoService,
    private readonly companyService: CompanyService,
    private readonly userService: UserSettingsService,
    private readonly router: Router,
    private readonly permissionsService: PermissionsService
  ) {}

  async selectCompany(
    company: GetCompanyListQuery['getCompanyList'][number],
    event: MouseEvent
  ): Promise<void> {
    event.stopPropagation();
    this.userService.cleanUiSettings()
    this.cognitoService.updateLoggedInUserLicenseId(company.licenseId);
    const role = await this.permissionsService.getUserRole();
    if (role) {
      await this.permissionsService.getPermissions(role);
    }
    const urlTree = this.router.parseUrl(this.router.url);
    const segments =
      urlTree.root.children['primary']?.segments.map((s) => s.path) || [];

    const idIndex = segments.findIndex((s) => /^([a-z])#/.test(s));

    if (idIndex !== -1) {
      const newSegments = segments.slice(0, idIndex);
      const newUrl = '/' + newSegments.join('/');
      await this.router.navigateByUrl(newUrl);
    } else {
      window.location.reload();
    }

    await this.userService.getUserUISettings();
    await this.userService.getUserAvatar();
  }

  private closeTimeout: any;

  closeMenuDelayed() {
    this.closeTimeout = setTimeout(() => {
      this.closeMenu();
    }, 300); // 300ms di margine
  }

  cancelCloseMenu() {
    clearTimeout(this.closeTimeout);
  }

  closeMenu() {
    if (this.menuTrigger) {
      this.menuTrigger.close();
    }
  }
}
